import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useRef,
} from "react";

import { UserMe } from "../../../models/auth";
import authApi from "../../../api/auth.api";
import {
    AUTH_SESSION_EXPIRED_EVENT,
    refreshManager,
} from "../../http/refresh.manager";

type SessionStatus =
    | "initializing"
    | "authenticated"
    | "unauthenticated";

interface AuthContextType {
    user: UserMe | null;
    status: SessionStatus;
    isAuthenticated: boolean;

    initSession: () => Promise<void>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useState<UserMe | null>(null);
    const [status, setStatus] = useState<SessionStatus>("initializing");

    const isAuthenticated = status === "authenticated";

    const initRef = useRef(false);
    const loadingRef = useRef(false);

    /**
     * =========================
     * INIT SESSION
     * =========================
     */
    const initSession = useCallback(async () => {
        if (loadingRef.current) return;

        loadingRef.current = true;
        setStatus("initializing");

        try {
            const response = await authApi.me();

            const data = response.data ?? null;
            const currentUser = data?.user ?? null;

            setUser(currentUser);
            setStatus(currentUser ? "authenticated" : "unauthenticated");
        } catch {
            setUser(null);
            setStatus("unauthenticated");
        } finally {
            loadingRef.current = false;
        }
    }, []);

    /**
     * =========================
     * LOGOUT
     * =========================
     */
    const logout = useCallback(async () => {
        try {
            await authApi.logout();
        } catch {
            // ignore : on nettoie localement de toute façon
        } finally {
            refreshManager.reset();
            setUser(null);
            setStatus("unauthenticated");
        }
    }, []);

    /**
     * =========================
     * BOOTSTRAP
     * =========================
     */
    useEffect(() => {
        if (initRef.current) return;

        initRef.current = true;
        void initSession();
    }, [initSession]);

    /**
     * =========================
     * ÉCOUTE SESSION EXPIRÉE
     * =========================
     */
    useEffect(() => {
        const handleExpired = () => {
            console.warn("[Auth] Session expired globally");
            refreshManager.reset();
            setUser(null);
            setStatus("unauthenticated");
        };

        window.addEventListener(
            AUTH_SESSION_EXPIRED_EVENT,
            handleExpired,
        );

        return () => {
            window.removeEventListener(
                AUTH_SESSION_EXPIRED_EVENT,
                handleExpired,
            );
        };
    }, []);

    /**
     * =========================
     * REFRESH AU FOCUS
     * =========================
     */
    useEffect(() => {
        const handleFocus = () => {
            if (!isAuthenticated) return;

            void authApi
                .me()
                .then((response) => {
                    const currentUser = response.data?.user ?? null;
                    if (currentUser) setUser(currentUser);
                })
                .catch(() => {
                    // interceptor gère le refresh ou l'event expired
                });
        };

        window.addEventListener("focus", handleFocus);

        return () => {
            window.removeEventListener("focus", handleFocus);
        };
    }, [isAuthenticated]);

    /**
     * =========================
     * REFRESH PROACTIF (10 min)
     * =========================
     * Évite les 401 en usage normal.
     * Ne refresh que si l'onglet est visible.
     */
    useEffect(() => {
        if (!isAuthenticated) return;

        const interval = setInterval(() => {
            if (document.visibilityState === "visible") {
                void authApi.me().catch(() => {
                    // silencieux : le manager + event gèrent les échecs
                });
            }
        }, 10 * 60 * 1000); // 10 min

        return () => clearInterval(interval);
    }, [isAuthenticated]);

    return (
        <AuthContext.Provider
            value={{
                user,
                status,
                isAuthenticated,
                initSession,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error("useAuth must be used within AuthProvider");
    }

    return context;
};